import { useState, useRef, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { VoiceRecorder, RecordingData } from 'capacitor-voice-recorder';
import { supabase } from '@/integrations/supabase/client';

interface UseVoiceRecorderOptions {
    maxDurationSeconds?: number;
    onTimeUpdate?: (seconds: number) => void;
    onMaxDurationReached?: () => void;
}

interface UseVoiceRecorderReturn {
    isRecording: boolean;
    isProcessing: boolean;
    duration: number;
    maxDuration: number;
    progress: number; // 0-100
    startRecording: () => Promise<boolean>;
    stopRecording: () => Promise<string | null>;
    cancelRecording: () => Promise<void>;
    hasPermission: boolean | null;
    requestPermission: () => Promise<boolean>;
    error: string | null;
}

const DEFAULT_MAX_DURATION = 120; // 2 minutes

export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}): UseVoiceRecorderReturn {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [duration, setDuration] = useState(0);
    const [maxDuration, setMaxDuration] = useState(options.maxDurationSeconds || DEFAULT_MAX_DURATION);
    const [hasPermission, setHasPermission] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startTimeRef = useRef<number>(0);

    // Fetch max duration from system settings
    useEffect(() => {
        const fetchMaxDuration = async () => {
            try {
                const { data } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'audio_recording_max_duration')
                    .single();

                if (data?.value) {
                    const parsed = parseInt(String(data.value), 10);
                    if (!isNaN(parsed) && parsed > 0) {
                        setMaxDuration(parsed);
                    }
                }
            } catch (err) {
                console.warn('Could not fetch audio max duration setting, using default');
            }
        };

        if (!options.maxDurationSeconds) {
            fetchMaxDuration();
        }
    }, [options.maxDurationSeconds]);

    // Check permission on mount
    useEffect(() => {
        checkPermission();
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const checkPermission = async () => {
        try {
            if (!Capacitor.isNativePlatform()) {
                // Web fallback - check navigator.mediaDevices
                if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                    setHasPermission(true);
                } else {
                    setHasPermission(false);
                }
                return;
            }

            const result = await VoiceRecorder.hasAudioRecordingPermission();
            setHasPermission(result.value);
        } catch (err) {
            console.error('Error checking audio permission:', err);
            setHasPermission(false);
        }
    };

    const requestPermission = async (): Promise<boolean> => {
        try {
            if (!Capacitor.isNativePlatform()) {
                // Web fallback
                try {
                    await navigator.mediaDevices.getUserMedia({ audio: true });
                    setHasPermission(true);
                    return true;
                } catch {
                    setHasPermission(false);
                    return false;
                }
            }

            const result = await VoiceRecorder.requestAudioRecordingPermission();
            setHasPermission(result.value);
            return result.value;
        } catch (err) {
            console.error('Error requesting audio permission:', err);
            setHasPermission(false);
            return false;
        }
    };

    const startTimer = useCallback(() => {
        startTimeRef.current = Date.now();
        setDuration(0);

        timerRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
            setDuration(elapsed);

            options.onTimeUpdate?.(elapsed);

            // Auto-stop when max duration reached
            if (elapsed >= maxDuration) {
                options.onMaxDurationReached?.();
                stopRecording();
            }
        }, 100); // Update every 100ms for smooth progress
    }, [maxDuration, options]);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const startRecording = async (): Promise<boolean> => {
        try {
            setError(null);

            // Check/request permission first
            if (!hasPermission) {
                const granted = await requestPermission();
                if (!granted) {
                    setError('Permissão de microfone negada');
                    return false;
                }
            }

            await VoiceRecorder.startRecording();
            setIsRecording(true);
            startTimer();
            return true;
        } catch (err: any) {
            console.error('Error starting recording:', err);
            setError(err.message || 'Erro ao iniciar gravação');
            return false;
        }
    };

    const stopRecording = async (): Promise<string | null> => {
        try {
            stopTimer();
            setIsProcessing(true);

            const result: RecordingData = await VoiceRecorder.stopRecording();
            setIsRecording(false);

            if (result.value && result.value.recordDataBase64) {
                return result.value.recordDataBase64;
            }

            return null;
        } catch (err: any) {
            console.error('Error stopping recording:', err);
            setError(err.message || 'Erro ao parar gravação');
            return null;
        } finally {
            setIsProcessing(false);
            setIsRecording(false);
        }
    };

    const cancelRecording = async (): Promise<void> => {
        try {
            stopTimer();

            if (isRecording) {
                await VoiceRecorder.stopRecording();
            }

            setIsRecording(false);
            setDuration(0);
            setError(null);
        } catch (err) {
            console.error('Error canceling recording:', err);
        }
    };

    const progress = maxDuration > 0 ? Math.min((duration / maxDuration) * 100, 100) : 0;

    return {
        isRecording,
        isProcessing,
        duration,
        maxDuration,
        progress,
        startRecording,
        stopRecording,
        cancelRecording,
        hasPermission,
        requestPermission,
        error,
    };
}
